<template>
  <div class="admin-container">
    <header>
      <div class="logo">
        <img src="https://i.postimg.cc/dtV0G4Fq/android-chrome-512x512.png" alt="Logo" />
        <h1>Admin Dashboard</h1>
      </div>
      <div class="header-right">
        <div class="current-time">{{ currentTime }}</div>
        <button @click="$emit('logout')" class="logout-button">
          <i class="fas fa-sign-out-alt"></i> Logout
        </button>
      </div>
    </header>
    <main>
      <section class="latest-user">
        <h2>Latest User Details</h2>
        <UserDetails :user="latestUser" :index="allUsers.length" @copy="showCopyPopup" />
      </section>
      <section class="all-users">
        <h2>All Users</h2>
        <div class="user-grid">
          <UserCard
            v-for="(user, index) in allUsersExceptLatest"
            :key="user.id"
            :user="user"
            :index="allUsers.length - index - 1"
            @show-popup="showPopup"
          />
        </div>
      </section>
    </main>
    <PopupOverlay
      v-if="showPopupOverlay"
      :user="selectedUser"
      :index="getSelectedUserIndex()"
      @close="closePopup"
      @copy="showCopyPopup"
    />
    <div v-if="showCopiedPopup" class="copied-popup">
      Copied to clipboard!
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { database } from '@/firebase';
import { ref as dbRef, onValue, off } from 'firebase/database';
import UserDetails from './UserDetails.vue';
import UserCard from './UserCard.vue';
import PopupOverlay from './PopupOverlay.vue';

export default {
  name: 'AdminDashboard',
  components: {
    UserDetails,
    UserCard,
    PopupOverlay,
  },
  emits: ['logout'],
  setup() {
    const currentTime = ref('');
    const allUsers = ref([]);
    const showPopupOverlay = ref(false);
    const selectedUser = ref(null);
    const showCopiedPopup = ref(false);

    const latestUser = computed(() => allUsers.value[0] || {});
    const allUsersExceptLatest = computed(() => allUsers.value.slice(1));

    const updateCurrentTime = () => {
      currentTime.value = new Date().toLocaleString();
    };

    let timer;

    onMounted(() => {
      updateCurrentTime();
      timer = setInterval(updateCurrentTime, 1000);

      const loginsRef = dbRef(database, 'logins');
      onValue(loginsRef, (snapshot) => {
        const logins = snapshot.val();
        if (logins) {
          allUsers.value = Object.entries(logins)
            .map(([id, data]) => ({ id, ...data }))
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        }
      });
    });

    onUnmounted(() => {
      clearInterval(timer);
      const loginsRef = dbRef(database, 'logins');
      off(loginsRef);
    });

    const showPopup = (user) => {
      selectedUser.value = user;
      showPopupOverlay.value = true;
    };

    const closePopup = () => {
      showPopupOverlay.value = false;
      selectedUser.value = null;
    };

    const getSelectedUserIndex = () => {
      return allUsers.value.findIndex(user => user.id === selectedUser.value?.id) + 1;
    };

    const showCopyPopup = () => {
      showCopiedPopup.value = true;
      setTimeout(() => {
        showCopiedPopup.value = false;
      }, 4000);
    };

    return {
      currentTime,
      latestUser,
      allUsers,
      allUsersExceptLatest,
      showPopup,
      closePopup,
      showPopupOverlay,
      selectedUser,
      getSelectedUserIndex,
      showCopyPopup,
      showCopiedPopup,
    };
  },
};
</script>

<style scoped>
.admin-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background-color: var(--card-background);
  border-radius: 15px;
  box-shadow: var(--card-shadow);
}

.logo {
  display: flex;
  align-items: center;
}

.logo img {
  width: 50px;
  height: 50px;
  margin-right: 15px;
}

.header-right {
  display: flex;
  align-items: center;
}

h1 {
  font-size: 28px;
  color: var(--primary-color);
  margin: 0;
}

.current-time {
  font-size: 18px;
  color: var(--text-color);
  margin-right: 20px;
}

.logout-button {
  padding: 10px 20px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s ease;
}

.logout-button:hover {
  background-color: #ff5252;
}

main {
  display: grid;
  gap: 30px;
}

section {
  background-color: var(--card-background);
  border-radius: 15px;
  padding: 25px;
  box-shadow: var(--card-shadow);
}

h2 {
  font-size: 24px;
  color: var(--secondary-color);
  margin-bottom: 20px;
  border-bottom: 2px solid var(--secondary-color);
  padding-bottom: 10px;
}

.user-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

@media (max-width: 768px) {
  header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-right {
    margin-top: 15px;
  }

  .current-time {
    margin-right: 0;
    margin-bottom: 10px;
  }

  .user-grid {
    grid-template-columns: 1fr;
  }
}

.copied-popup {
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: var(--secondary-color);
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1000;
}
</style>

